import { useCallback, useEffect, useMemo, useState } from 'react'
import { createBrowserBridge, isE2EMode } from '../e2e/browser-bridge'
import { GameRuntime } from '../game/runtime'
import { navigate } from '../navigation'
import { type GameplayCameraMode, nextCameraMode } from '../scene/FollowCamera'
import { GameCanvas } from '../scene/GameCanvas'
import { Hud } from '../ui/Hud'
import { MobileControls } from '../ui/MobileControls'
import { PauseMenu } from '../ui/PauseMenu'
import { ResultsScreen } from '../ui/ResultsScreen'
import { StartupLoadingScreen } from '../ui/StartupLoadingScreen'
import { TitleScreen } from '../ui/TitleScreen'
import { ErrorBoundary } from './ErrorBoundary'
import { PlayerInputRouter } from './input-family'
import type { StartupPreparationState } from './startup-preparation'
import { useAudio } from './use-audio'
import { useControls } from './use-controls'
import { useRuntimeState } from './use-runtime-state'

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setReduced(query.matches)
    update()
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])
  return reduced
}

export function isTouchCapable(): boolean {
  if (navigator.maxTouchPoints > 0) return true
  return typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches
}

export function App() {
  const runtime = useMemo(() => new GameRuntime(), [])
  const inputRouter = useMemo(() => new PlayerInputRouter(runtime), [runtime])
  const state = useRuntimeState(runtime)
  const reducedMotion = useReducedMotion()
  const e2eMode = useMemo(() => isE2EMode(window.location.search), [])
  const [started, setStarted] = useState(false)
  const [paused, setPaused] = useState(false)
  const [muted, setMuted] = useState(false)
  const [cameraMode, setCameraMode] = useState<GameplayCameraMode>('chase')
  const [preparation, setPreparation] = useState<StartupPreparationState>('preparing')
  const [preparationRevision, setPreparationRevision] = useState(0)
  const [startRequested, setStartRequested] = useState(false)
  const touchCapable = useMemo(isTouchCapable, [])

  useEffect(() => runtime.pause(), [runtime])
  useEffect(() => {
    if (!e2eMode) return
    const previousBridge = window.__TRIDE_E2E__
    const bridge = createBrowserBridge(runtime)
    window.__TRIDE_E2E__ = bridge
    return () => {
      if (window.__TRIDE_E2E__ !== bridge) return
      if (previousBridge === undefined) delete window.__TRIDE_E2E__
      else window.__TRIDE_E2E__ = previousBridge
    }
  }, [e2eMode, runtime])
  const { unlockAudio } = useAudio(runtime, started, muted)

  const retryPreparation = useCallback(() => {
    setPreparation('preparing')
    setPreparationRevision((current) => current + 1)
  }, [])

  const requestRaceStart = useCallback(() => {
    unlockAudio()
    setStartRequested(true)
    if (preparation === 'failed') retryPreparation()
  }, [preparation, retryPreparation, unlockAudio])

  useEffect(() => {
    if (preparation === 'ready' && startRequested && !started) setStarted(true)
  }, [preparation, startRequested, started])

  useEffect(() => {
    if (preparation !== 'ready' || !startRequested || !started) return
    let frameId = requestAnimationFrame(() => {
      frameId = requestAnimationFrame(() => {
        frameId = 0
        runtime.restart()
        setPaused(false)
        setStartRequested(false)
      })
    })
    return () => {
      if (frameId !== 0) cancelAnimationFrame(frameId)
    }
  }, [preparation, runtime, startRequested, started])

  const handlePrepared = useCallback(() => {
    setPreparation((current) => (current === 'preparing' ? 'ready' : current))
  }, [])

  const handlePreparationError = useCallback(() => {
    setPreparation((current) => (current === 'preparing' ? 'failed' : current))
  }, [])

  const handleCanvasError = useCallback(() => {
    runtime.pause()
    setPreparation('failed')
    setStarted(false)
    setPaused(false)
    setStartRequested(true)
  }, [runtime])

  const restartRace = useCallback(() => {
    unlockAudio()
    runtime.restart()
    setPaused(false)
    setStarted(true)
  }, [runtime, unlockAudio])

  const togglePause = useCallback(() => {
    if (paused) {
      unlockAudio()
      runtime.resume()
    } else runtime.pause()
    setPaused(!paused)
  }, [paused, runtime, unlockAudio])

  const cycleCamera = useCallback(() => {
    setCameraMode(nextCameraMode)
  }, [])

  const toggleMute = useCallback(() => {
    unlockAudio()
    setMuted((current) => !current)
  }, [unlockAudio])

  const controlActions = useMemo(() => ({ onPause: togglePause, onCamera: cycleCamera }), [cycleCamera, togglePause])
  const raceActive = started && !startRequested
  useControls(inputRouter, raceActive && state.race.phase !== 'finished', controlActions)

  useEffect(() => {
    if (started) return
    const onEnter = (event: KeyboardEvent) => {
      if (event.code === 'Enter') requestRaceStart()
    }
    window.addEventListener('keydown', onEnter)
    return () => window.removeEventListener('keydown', onEnter)
  }, [requestRaceStart, started])

  return (
    <main className="game-shell">
      <div className="canvas-shell" aria-hidden="true">
        <ErrorBoundary key={preparationRevision} onError={handleCanvasError}>
          <GameCanvas
            runtime={runtime}
            cameraMode={started ? cameraMode : 'showcase'}
            reducedMotion={reducedMotion}
            manualSimulation={e2eMode}
            onPrepared={handlePrepared}
            onPreparationError={handlePreparationError}
          />
        </ErrorBoundary>
      </div>
      {!started && !startRequested && (
        <TitleScreen
          muted={muted}
          preparation={preparation}
          onStart={requestRaceStart}
          onMultiplayer={() => navigate('/multiplayer')}
          onToggleMute={toggleMute}
        />
      )}
      {startRequested && (
        <StartupLoadingScreen state={preparation === 'ready' ? 'arming' : preparation} onRetry={retryPreparation} />
      )}
      {started && state.race.phase !== 'finished' && <Hud runtime={runtime} cameraMode={cameraMode} />}
      {raceActive && !paused && state.race.phase !== 'finished' && touchCapable && (
        <MobileControls router={inputRouter} onPause={togglePause} onCamera={cycleCamera} />
      )}
      {raceActive && paused && <PauseMenu onResume={togglePause} onRestart={restartRace} />}
      {started && state.race.phase === 'finished' && <ResultsScreen state={state} onRestart={restartRace} />}
      <div className="scanlines" aria-hidden="true" />
    </main>
  )
}
